import { Builder as DeviceBuilder } from './Device'
import type { ChannelDetail, IDevice } from '@core/devices/IDevice'
import { ChannelType } from '@core/devices/IDevice'
import { CloudAdapter } from '@/netadapters/CloudAdapter'
import { INSTANCE as DeviceChecker } from '@core/devices/DeviceConnectionChecker'
import { throttle, differenceBy } from 'lodash'
import { Env } from '@core/EnvUtils'
import { TimeConstants } from '@core/Constants'
import { INSTANCE as NotificationManager } from '@/core/Notification/NotificationManager'
import { Action } from '../databus/IMessage'
import { i18n } from '@/i18n'

const { t } = i18n.global

export interface ChangeListener {
  /**
   * Called when initialized
   */
  onInitialized?(): void

  /**
   * Called when device list updated
   */
  onDeviceListUpdated(): void

  /**
   * Called when current selected device changed.
   *
   * @param device the current selected device. Or undefined when unset current selected device.
   */
  onCurrentSelectedDeviceChanged(device: IDevice | undefined): void
}

export interface IReadOnlyDeviceManager {
  getDeviceById(deviceId: string): IDevice | undefined

  getDeviceList(): IDevice[]

  getCurrentSelectedDevice(): IDevice | undefined

  setCurrentSelectedDevice(deviceId: string): Promise<boolean>

  unsetCurrentSelectedDevice(): void

  addChangeListener(listener: ChangeListener): void

  removeChangeListener(listener: ChangeListener): void
}

export interface IDeviceManager extends IReadOnlyDeviceManager {
  readonly readonlyInstance: IReadOnlyDeviceManager

  /**
   * @internal
   * @param list of {@link Device}
   */
  updateDeviceList(list: IDevice[] | undefined): void
}

// export for test only
export class DeviceManager implements IDeviceManager {
  #adapter = new CloudAdapter()
  #changeListeners = new Set<ChangeListener>()
  #deviceMap = new Map<string, IDevice>()
  #currentDevice: IDevice | undefined
  #initialized = false
  #notifyDeviceListChanged

  constructor() {
    this.#notifyDeviceListChanged = throttle(
      () => {
        for (const device of this.getDeviceList()) {
          if (!device.canCallService()) {
            console.log(
              `[updateDeviceList]: Unable to call service ${new Date().toLocaleString()}`,
              device.model,
              device.getAllChannelInfo()
            )
          }
        }

        for (const listener of this.#changeListeners) {
          try {
            listener.onDeviceListUpdated()
          } catch (error) {
            // 在调用微应用回调的时候，主动抛出捕获错误，同时在release环境让运行错误不阻塞程序运行
            if (!Env.isRelease()) {
              throw error
            }
          }
        }
      },
      TimeConstants.DEFAULT_THROTTLING_IN_MS,
      {
        leading: false,
        trailing: true,
      }
    )

    DeviceChecker.setListener((device, channel) => {
      this.updateDeviceConnectionStates(device, channel)
    })
  }

  getDeviceById(deviceId: string): IDevice | undefined {
    return this.#deviceMap.get(deviceId)
  }

  getDeviceList(): IDevice[] {
    return Array.from(this.#deviceMap.values())
  }

  getCurrentSelectedDevice(): IDevice | undefined {
    return this.#currentDevice
  }

  async updateDeviceConnectionStates(device: IDevice, channel: ChannelDetail) {
    const currentDevice = this.#deviceMap.get(device.id)

    if (!currentDevice) return

    currentDevice.updateChannelAvailability(channel)

    if (channel.type === ChannelType.WAN) this.#notifyDeviceListChanged()
  }

  #showVersionNotice(device: IDevice) {
    if (device.isDisabledTransmissionFile) {
      NotificationManager.addSystemInfo({
        msg: t('owork_base.device_version_tips', { device: device.model }),
        action: Action.CONTENT_PROMPT,
        isEmphasized: true,
      })
    }
  }

  #removeDevice(list: IDevice[] | undefined): boolean {
    if (!list || list.length === 0) return false
    let hasChanged = false
    const currentDeviceList = this.getDeviceList()

    const removedList = differenceBy(currentDeviceList, list, 'id')

    for (const device of removedList) {
      this.#deviceMap.delete(device.id)
      if (!hasChanged) hasChanged = true
    }

    return hasChanged
  }

  async updateDeviceList(list: IDevice[] | undefined): Promise<void> {
    if (!list || list.length === 0) {
      this.#reset()

      return
    }

    let hasChanged = this.#removeDevice(list)

    for (const device of list) {
      console.log(
        `[updateDeviceList]: device update info ${new Date().toLocaleString()}`,
        device,
        device.model,
        device.canCallService()
      )

      const currentDevice = this.#deviceMap.get(device.id)

      // TODO：diff移入updateDeviceMap判断
      if (currentDevice) {
        device.copyChannelAvailabilityWithSameHost(currentDevice)
        if (device.equals(currentDevice)) continue
        await this.#updateDeviceMap(device, currentDevice.accessToken)
      } else {
        this.#showVersionNotice(device)
        await this.#updateDeviceMap(device)
      }

      if (!hasChanged) hasChanged = true
    }

    if (this.#currentDevice) {
      // previous set a device
      const device = this.#deviceMap.get(this.#currentDevice.id)

      if (device) {
        if (this.#currentDevice.equals(device)) {
          if (!this.#currentDevice.accessToken) this.#currentDevice.updateAccessToken(device.accessToken)
        } else {
          this.#currentDevice = device
          this.#notifyCurrentSelectedDeviceChanged()
        }
      } else {
        // previous selected device be removed while device list updated
        this.#currentDevice = undefined
        this.#notifyCurrentSelectedDeviceChanged()
      }
    }

    if (hasChanged) {
      DeviceChecker.start(this.getDeviceList())
      this.#notifyDeviceListChanged()
    }
  }

  #reset() {
    if (this.#deviceMap.size === 0) return

    this.#deviceMap.clear()

    if (this.#currentDevice) {
      this.#currentDevice = undefined
      this.#notifyCurrentSelectedDeviceChanged()
    }

    this.#notifyDeviceListChanged()
    DeviceChecker.stop()
  }

  clear() {
    this.#deviceMap.clear()
    this.#currentDevice = undefined
    DeviceChecker.stop()
  }

  async #updateDeviceMap(device: IDevice, token?: string): Promise<void> {
    this.#deviceMap.set(device.id, device)
    await device.updateAccessToken(token)
  }

  addChangeListener(listener: ChangeListener): void {
    if (!this.#changeListeners.has(listener)) {
      this.#changeListeners.add(listener)

      if (this.#initialized) {
        listener.onInitialized && listener.onInitialized()
        listener.onDeviceListUpdated()
      }
    }
  }

  removeChangeListener(listener: ChangeListener): void {
    this.#changeListeners.delete(listener)
  }

  async setCurrentSelectedDevice(deviceId: string): Promise<boolean> {
    if (this.#currentDevice?.id === deviceId || !this.#deviceMap.has(deviceId)) return false

    this.#currentDevice = this.#deviceMap.get(deviceId)

    if (!this.#currentDevice) return false

    this.#notifyCurrentSelectedDeviceChanged()

    return true
  }

  unsetCurrentSelectedDevice(): void {
    if (this.#currentDevice) {
      this.#currentDevice = undefined
      this.#notifyCurrentSelectedDeviceChanged()
    }
  }

  async initDeviceList() {
    const deviceList = await this.#adapter.getDeviceList()

    if (!deviceList) return

    const result = []

    for (const deviceBean of deviceList) {
      const device = DeviceBuilder.build(deviceBean)

      console.log(
        `[updateDeviceList]: device init info ${new Date().toLocaleString()}`,
        device,
        device.model,
        device.canCallService()
      )

      this.#showVersionNotice(device)
      this.#deviceMap.set(device.id, device)

      result.push(device.updateAccessToken())
    }

    Promise.all(result)
      .then(() => {
        DeviceChecker.start(this.getDeviceList())
        this.#initialized = true
        this.#notifyInitialized()
        this.#notifyDeviceListChanged()
      })
      .catch((error) => {
        console.error(`init device list error`, error)
      })
  }

  get readonlyInstance(): IReadOnlyDeviceManager {
    return {
      getDeviceById: (deviceId: string) => INSTANCE.getDeviceById(deviceId),
      getDeviceList: () => this.getDeviceList(),
      getCurrentSelectedDevice: () => this.getCurrentSelectedDevice(),
      setCurrentSelectedDevice: async (deviceId: string) => await this.setCurrentSelectedDevice(deviceId),
      unsetCurrentSelectedDevice: () => this.unsetCurrentSelectedDevice(),
      addChangeListener: (listener: ChangeListener) => this.addChangeListener(listener),
      removeChangeListener: (listener: ChangeListener) => this.removeChangeListener(listener),
    }
  }

  #notifyCurrentSelectedDeviceChanged() {
    for (const listener of this.#changeListeners) {
      try {
        listener.onCurrentSelectedDeviceChanged(this.#currentDevice)
      } catch (error) {
        if (!Env.isRelease()) {
          throw error
        }
      }
    }
  }

  #notifyInitialized() {
    for (const listener of this.#changeListeners) {
      try {
        listener.onInitialized && listener.onInitialized()
      } catch (error) {
        if (!Env.isRelease()) {
          throw error
        }
      }
    }
  }
}

const INSTANCE = new DeviceManager()

export { INSTANCE }
